import { useEffect, useState } from "react";

import {
  Button,
  Table,
  TableMetadata,
  useTable,
  Typography,
  ColumnSortEnum,
  TextField,
  Select,
  Modal,
  TableStateWithFilters,
  AutocompleteMultipleSelect,
  Tooltip,
  IconWrapper,
  Checkbox
} from "@king-ict/eupisi-ui/components";
import { ReactComponent as CloseIcon } from "@king-ict/eupisi-ui/components/Icon/Close.svg";
import { ReactComponent as DownloadIcon } from "@king-ict/eupisi-ui/components/Icon/Download.svg";
import { ReactComponent as EditIcon } from "@king-ict/eupisi-ui/components/Icon/Edit.svg";
import { ReactComponent as InfoIcon } from "@king-ict/eupisi-ui/components/Icon/Info.svg";
import { ReactComponent as RemoveIcon } from "@king-ict/eupisi-ui/components/Icon/RemoveIcon.svg";
import { useValidation } from "@king-ict/eupisi-ui/hooks";
import { CodeBook, ExportTypeEnum, PagedResult } from "@king-ict/eupisi-ui/models";
import { codeBookToSelectData, nameof } from "@king-ict/eupisi-ui/utils";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";

import { searchUsers, searchInstitutionsCodeBook, updateUserRequest, deleteUserRequest } from "../../api/nisooApi";
import { ExportUsersRequest } from "../../api/User/ExportUsersRequest";
import { SearchUserRequest } from "../../api/User/SearchUserRequest";
import { exportUsers } from "../../api/User/userApi";
import { UserRequestSearch } from "../../api/User/UserRequestSearch";
import { useDidUpdateEffect, useInput } from "../../hooks";
import { SortDirection } from "../../models/Shared/SortDirection";
import { UserEditItem, userEditItemInitial, UserListItem, EditUserValidationScheme } from "../../models/User";
import { UserInfo } from "../../models/User";
import { RoleEnum } from "../../models/User/RoleEnum";
import { UserSort } from "../../models/User/UserSort";
import { useGetUserRolesQuery } from "../../queries/codeBookQuery";
import { useGetUserSessionQuery } from "../../queries/userQuery";
import { createUserRoute } from "../../routes/routes";
import styles from "./UserRole.module.scss";

export function UserRole() {
  const storageKey = 'UserSearchTableState';
  const createInitialTableState = (): TableStateWithFilters<SearchUserRequest> => {
    const sessionStorageTableState = sessionStorage.getItem(storageKey);
    if (sessionStorageTableState === null) {
      return {
        institutionIds: [],
        institutions: [],
        firstName: "",
        lastName: "",
        oib: "",
        userRoles: [],
        page: 1,
        pageSize: 10,
        sortBy: UserSort.UserName,
        order: ColumnSortEnum.ASC
      };
    }

    var result = JSON.parse(sessionStorageTableState) as TableStateWithFilters<SearchUserRequest>;
    return result;
  }

  const createInitialUserListSearch = (initialTableState: TableStateWithFilters<SearchUserRequest>): SearchUserRequest => {
    return {
      institutionIds: initialTableState.institutionIds,
      institutions: initialTableState.institutionIds.map(ins => ins.id),
      firstName: initialTableState.firstName,
      lastName: initialTableState.lastName,
      oib: initialTableState.oib,
      userRoles: initialTableState.userRoles,
      page: 1,
      pageSize: 10,
      sortBy: UserSort.UserName,
      sortDirection: SortDirection.Asc
    }
  }

  const createInitialUserFilter = (initialTableState: TableStateWithFilters<UserRequestSearch>): UserRequestSearch => {
    return {
      institutionIds: initialTableState.institutionIds,
      institutions: initialTableState.institutionIds.map(ins => ins.id),
      firstName: initialTableState.firstName,
      lastName: initialTableState.lastName,
      oib: initialTableState.oib,
      userRoles: initialTableState.userRoles
    }
  }

  const createInitialUserList = (): PagedResult<UserListItem> => {
    return {
      currentPage: 1,
      firstRowOnPage: 0,
      lastRowOnPage: 0,
      pageCount: 0,
      pageSize: 10,
      rowCount: 0,
      results: [],
    }
  }

  const [initialTableState] = useState<TableStateWithFilters<SearchUserRequest>>(() => createInitialTableState());
  const [filteredInsitutions, setFilteredInsitutions] = useState<CodeBook[]>([] as CodeBook[]);
  const [userList, setUserList] = useState<PagedResult<UserListItem>>(() => createInitialUserList());
  const [lastUsedSearch, setLastUsedSearch] = useState<SearchUserRequest>();
  const [userSearch, setUserSearch] = useState<UserRequestSearch>(() => createInitialUserFilter(initialTableState));
  const [currentUserId, setCurrentUserId] = useState(0);
  const [editUserItem, setEditUserItem] = useState<UserEditItem>(userEditItemInitial());
  const [openDeleteConfirmModal, setOpenDeleteConfirmModal] = useState(false);
  const [openEditConfirmModal, setOpenEditConfirmModal] = useState(false);
  const [openSuccessModal, setOpenSuccessModal] = useState(false);
  const { data: userSession } = useGetUserSessionQuery();

  const [errors, doValidations] = useValidation({
    data: editUserItem,
    validationSchema: EditUserValidationScheme
  });

  const history = useHistory();
  const { t } = useTranslation();
  const { handleChange } = useInput();
  const { data: userRoles } = useGetUserRolesQuery();

  const userListTableMetadata: TableMetadata<UserListItem> = {
    rowKey: "userId",
    columns: [
      {
        columnKey: "isActive",
        header: "",
        accessor: (item) => {
          if (item && item.isActive) {
            return undefined;
          }
          else {
            return (
              <div className={"text-center"}>
                <CloseIcon
                  className={styles.isActiveButton}
                />
              </div>)
          }
        }
      },
      {
        columnKey: "institutionName",
        header: t("institution.institutionName"),
        accessor: (item: UserListItem) => item.institutionName,
        color: "success",
        sortBy: UserSort.InstitutionName
      },
      {
        columnKey: "userName",
        header: t("common.fullName"),
        accessor: (item: UserListItem) => item.userName,
        sortBy: UserSort.UserName
      },
      {
        columnKey: "programHistoryDescription",
        header: t("common.description"),
        accessor: (item) => {
          if (item.phone || item.email) {
            return (
              <Tooltip
                overlay={<div className={styles.tooltipWindow}>{formatUserContact(item)}</div>}
                placement="bottom"
                flip
              >
                <IconWrapper color="primary">
                  <InfoIcon />
                </IconWrapper>
              </Tooltip>
            );
          }
          else {
            return "";
          }
        }
      },
      {
        columnKey: "oib",
        header: t("common.oib"),
        accessor: (item: UserListItem) => item.oib,
        sortBy: UserSort.UserOib
      },
      {
        columnKey: "roleName",
        header: t("common.userRole"),
        accessor: "roleName",
        sortBy: UserSort.UserRole
      },
      {
        columnKey: "editUser",
        header: "",
        accessor: (item) => {
          return (<div className={"text-right"}>
            <EditIcon
              className={styles.deleteButton}
              onClick={() => handleEditUser(item.userId)}
            />
          </div>)
        }
      },
      {
        columnKey: "deleteUser",
        header: "",
        accessor: (item) => {
          if (canDelete(item)) {
            return (<div className={"text-right"}>
              <RemoveIcon
                className={styles.deleteButton}
                onClick={() => handleDeleteUser(item.userRoleId)}
              />
            </div>)
          }
        }
      }
    ],
    rowToolTip: () => undefined
  };


  if (userSession && (userSession.roleId === RoleEnum.ASOO || userSession.roleId === RoleEnum.ASOOAdmin || userSession.roleId === RoleEnum.ASOOOffice
    || userSession.roleId === RoleEnum.MZOM || userSession.roleId === RoleEnum.MZOMAdmin || userSession.roleId === RoleEnum.MZOMOffice)) {
    var isActiveCol = userListTableMetadata.columns[0];
    userListTableMetadata.columns.shift();
    userListTableMetadata.columns.shift();
    userListTableMetadata.columns.unshift(isActiveCol);
  }

  const formatUserContact = (user: UserListItem) => {
    let userContact;

    if (user.phone) {
      userContact = `${t("userRole.phone")}: ${user.phone}`;
    }
    if (user.email) {
      userContact += ` ${t("userRole.email")}: ${user.email}`;
    }
    return userContact?.trim();
  }

  const handleDeleteUser = (id: number) => {
    setCurrentUserId(id);
    setOpenDeleteConfirmModal(true);
  };

  const handleEditUser = (id: number) => {

    var currentUser = userList.results.find(usr => usr.userId === id);

    editUserItem.userId = id;
    if (currentUser) {
      editUserItem.email = currentUser.email;
      editUserItem.phone = currentUser.phone;
      editUserItem.roleId = currentUser.roleId;
      editUserItem.isActive = currentUser.isActive;
    }

    setEditUserItem(editUserItem);

    setOpenEditConfirmModal(true);
  };

  var [tableProps, tableState, setFilters] = useTable<UserRequestSearch>(
    {
      metadata: userListTableMetadata,
      data: userList.results,
      pagination: true,
      paginationPage: initialTableState.page,
      paginationPageSize: initialTableState.pageSize as any,
      totalItems: userList.rowCount,
      defaultSort: { order: initialTableState.order, sortBy: initialTableState.sortBy }
    }
  );

  const convertUserSearchToApiRequest = (userSearch: UserRequestSearch) => {
    var request = {
      institutionIds: userSearch.institutionIds.map(ins => ins),
      institutions: userSearch.institutionIds.map(ins => ins.id),
      firstName: userSearch.firstName,
      lastName: userSearch.lastName,
      oib: userSearch.oib,
      userRoles: userSearch.userRoles.map(ur => ur),
      sortBy: tableState.sortBy ?? UserSort.UserName,
      sortDirection: tableState.order && tableState.order === ColumnSortEnum.ASC ? SortDirection.Asc : SortDirection.Desc,
      page: tableState.page ?? 1,
      pageSize: tableState.pageSize ?? 10
    };
    return request;
  }

  useEffect(() => {
    if (!userSession || userSession.roleId == RoleEnum.None) {
      return;
    }

    const searchUserList = async () => {
      var request = convertUserSearchToApiRequest(userSearch);
      setLastUsedSearch(request);
      var results = await searchUsers(request);
      setUserList(createInitialUserList());

      setUserList(results);
    }
    searchUserList();
    // userSearch change shouldn't trigger this fetch
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSession, tableState]);

  useDidUpdateEffect(() => {
    sessionStorage.setItem(storageKey, JSON.stringify({
      ...userSearch,
      sortBy: tableState.sortBy ?? UserSort.UserName,
      order: tableState.order ?? ColumnSortEnum.ASC,
      page: tableState.page ?? 1,
      pageSize: tableState.pageSize ?? 10
    }));
  }, [tableState])

  const handleInstitutionFilterChange = async (filter: string) => {
    var result = await searchInstitutionsCodeBook(filter, [], []);
    setFilteredInsitutions(result);
  }

  const handleDeleteConfirmModal = async () => {
    setOpenDeleteConfirmModal(false);
    await deleteUserRequest(currentUserId).then(() => {
      setFilters(createInitialUserListSearch(initialTableState));
      search();
    });
  }

  const handleEditConfirmModal = async () => {

    const isValid = doValidations();
    if (!isValid) {
      console.log(errors);
      return;
    }

    const userInfo: UserInfo = {
      userId: editUserItem.userId,
      oib: "",
      institutionId: 0,
      institutionName: "",
      firstName: "",
      lastName: "",
      phone: editUserItem.phone,
      email: editUserItem.email,
      roleId: editUserItem.roleId,
      roleName: '',
      isActive: editUserItem.isActive
    };

    await updateUserRequest(userInfo).then(() => {
      setFilters(createInitialUserListSearch(initialTableState));
      search();
    });

    setOpenSuccessModal(true);
  }

  const handleCancelDeleteModal = async () => {
    setOpenDeleteConfirmModal(false);
  }

  const handleCancelEditModal = async () => {
    setOpenEditConfirmModal(false);
  }

  const handleCloseSuccessModal = async () => {
    setOpenSuccessModal(false);
    setOpenEditConfirmModal(false);
  }

  const clearFilters = async () => {
    const emptySearch = {
      institutionIds: [],
      institutions: [],
      firstName: '',
      lastName: '',
      oib: '',
      userRoles: [],
    };
    setUserSearch(emptySearch);
    setFilters(emptySearch);
  }

  const search = async () => {
    setFilters(userSearch);
  };

  const onAddUser = () => {
    history.push(createUserRoute());
  };

  const download = async (exportType: ExportTypeEnum) => {
    if (!lastUsedSearch || !userSession) {
      return;
    }

    const request: ExportUsersRequest = {
      institutionIds: lastUsedSearch.institutionIds.map(item => item.id),
      firstName: lastUsedSearch.firstName,
      lastName: lastUsedSearch.lastName,
      oib: lastUsedSearch.oib,
      roleIds: lastUsedSearch.userRoles.map(item => item.id),
      sortBy: lastUsedSearch.sortBy,
      sortDirection: lastUsedSearch.sortDirection,
      exportType: exportType
    }

    await exportUsers(request);
  }

  const canEdit = (editItem: UserEditItem) => {
    if (userSession?.roleId === RoleEnum.InstitutionAdmin) {
      if (editItem.roleId === RoleEnum.InstitutionAdmin) {
        return true;
      }
      return false;
    }
    if (userSession?.roleId === RoleEnum.InstitutionDirector) {
      if (editItem.roleId === RoleEnum.InstitutionAdmin || editItem.roleId === RoleEnum.InstitutionDirector) {
        return true;
      }
      return false;
    }
    if (userSession?.roleId === RoleEnum.ASOO) {
      if (editItem.roleId === RoleEnum.ASOO) {
        return true;
      }
      return false;
    }
    if (userSession?.roleId === RoleEnum.ASOOAdmin) {
      if (editItem.roleId === RoleEnum.ASOO || editItem.roleId === RoleEnum.ASOOAdmin) {
        return true;
      }
      return false;
    }
    if (userSession?.roleId === RoleEnum.ASOOOffice) {
      if (editItem.roleId === RoleEnum.ASOOOffice) {
        return true;
      }
      return false;
    }
    if (userSession?.roleId === RoleEnum.MZOM) {
      if (editItem.roleId === RoleEnum.MZOM) {
        return true;
      }
      return false;
    }
    if (userSession?.roleId === RoleEnum.MZOMAdmin) {
      if (editItem.roleId === RoleEnum.MZOM || editItem.roleId === RoleEnum.MZOMAdmin) {
        return true;
      }
      return false;
    }
    if (userSession?.roleId === RoleEnum.MZOMOffice) {
      if (editItem.roleId === RoleEnum.MZOMOffice) {
        return true;
      }
      return false;
    }
    if (userSession?.roleId === RoleEnum.Helpdesk) {
      return true;
    }
    return false;
  }

  const canEditRole = (editItem: UserEditItem) => {
    if (userSession?.roleId === RoleEnum.InstitutionDirector) {
      if (editItem.roleId === RoleEnum.InstitutionAdmin || editItem.roleId === RoleEnum.InstitutionDirector) {
        return true;
      }
    }
    if (userSession?.roleId === RoleEnum.ASOOAdmin) {
      if (editItem.roleId === RoleEnum.ASOO || editItem.roleId === RoleEnum.ASOOAdmin) {
        return true;
      }
    }
    if (userSession?.roleId === RoleEnum.MZOMAdmin) {
      if (editItem.roleId === RoleEnum.MZOM || editItem.roleId === RoleEnum.MZOMAdmin) {
        return true;
      }
    }
    if (userSession?.roleId === RoleEnum.Helpdesk) {
      if (editItem.roleId === RoleEnum.ASOOOffice || editItem.roleId === RoleEnum.MZOMOffice || editItem.roleId === RoleEnum.Helpdesk) {
        return false;
      }
      else {
        return true;
      }
    }
    return false;
  }

  const canDelete = (item: UserListItem) => {
    if (userSession?.userId === item.userId) {
      return false;
    }
    if (userSession?.roleId === RoleEnum.InstitutionAdmin) {
      if (item.roleId === RoleEnum.InstitutionAdmin) {
        return true;
      }
      return false;
    }
    if (userSession?.roleId === RoleEnum.InstitutionDirector) {
      if (item.roleId === RoleEnum.InstitutionAdmin || item.roleId === RoleEnum.InstitutionDirector) {
        return true;
      }
      return false;
    }
    if (userSession?.roleId === RoleEnum.ASOO) {
      if (item.roleId === RoleEnum.ASOO) {
        return true;
      }
      return false;
    }
    if (userSession?.roleId === RoleEnum.ASOOAdmin) {
      if (item.roleId === RoleEnum.ASOO || item.roleId === RoleEnum.ASOOAdmin) {
        return true;
      }
      return false;
    }
    if (userSession?.roleId === RoleEnum.ASOOOffice) {
      if (item.roleId === RoleEnum.ASOOOffice) {
        return true;
      }
      return false;
    }
    if (userSession?.roleId === RoleEnum.MZOM) {
      if (item.roleId === RoleEnum.MZOM) {
        return true;
      }
      return false;
    }
    if (userSession?.roleId === RoleEnum.MZOMAdmin) {
      if (item.roleId === RoleEnum.MZOM || item.roleId === RoleEnum.MZOMAdmin) {
        return true;
      }
      return false;
    }
    if (userSession?.roleId === RoleEnum.MZOMOffice) {
      if (item.roleId === RoleEnum.MZOMOffice) {
        return true;
      }
      return false;
    }
    if (userSession?.roleId === RoleEnum.Helpdesk) {
      return true;
    }
    return false;
  }

  const getUserRolesFilter = () => {
    if (userSession?.roleId === RoleEnum.ASOOAdmin) {
      return userRoles?.filter(ur => ur.id === RoleEnum.ASOO || ur.id === RoleEnum.ASOOAdmin || ur.id === RoleEnum.ASOOOffice);
    }
    if (userSession?.roleId === RoleEnum.MZOMAdmin) {
      return userRoles?.filter(ur => ur.id === RoleEnum.MZOM || ur.id === RoleEnum.MZOMAdmin || ur.id === RoleEnum.MZOMOffice);
    }
    return userRoles;
  }

  const getUserRolesForUser = (editItem: UserEditItem) => {
    if (editItem.roleId === RoleEnum.InstitutionAdmin || editItem.roleId === RoleEnum.InstitutionDirector) {
      return userRoles?.filter(ur => ur.id === RoleEnum.InstitutionAdmin || ur.id === RoleEnum.InstitutionDirector);
    }
    if (editItem.roleId === RoleEnum.ASOO || editItem.roleId === RoleEnum.ASOOAdmin) {
      return userRoles?.filter(ur => ur.id === RoleEnum.ASOO || ur.id === RoleEnum.ASOOAdmin);
    }
    if (editItem.roleId === RoleEnum.ASOOOffice) {
      return userRoles?.filter(ur => ur.id === RoleEnum.ASOOOffice);
    }

    if (editItem.roleId === RoleEnum.MZOM || editItem.roleId === RoleEnum.MZOMAdmin) {
      return userRoles?.filter(ur => ur.id === RoleEnum.MZOM || ur.id === RoleEnum.MZOMAdmin);
    }
    if (editItem.roleId === RoleEnum.MZOMOffice) {
      return userRoles?.filter(ur => ur.id === RoleEnum.MZOMOffice);
    }
    if (editItem.roleId === RoleEnum.Helpdesk) {
      return userRoles;
    }
    return undefined;
  }

  return (
    <>
      <Row>
        <Col md={12}>
          <h4>{t("navigation.userRoles")}</h4>
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <Button outline onClick={onAddUser} className={styles.actionButton}>
            {t("userRole.newUser")}
          </Button>
        </Col>
      </Row>
      <>
        <Row>
          {userSession && userSession.roleId === RoleEnum.Helpdesk &&
            <Col md={8}>
              <AutocompleteMultipleSelect<CodeBook>
                label={t("common.institution")}
                name={nameof<typeof userSearch>("institutionIds")}
                value={userSearch.institutionIds}
                valueProp="id"
                textProp="name"
                onChange={handleChange(setUserSearch)}
                selectOptions={filteredInsitutions}
                onFilterChange={handleInstitutionFilterChange}
                maximumSelectedItems={10}
              />
            </Col>
          }
          <Col md={2}>
            <TextField
              label={t("common.firstName")}
              name={nameof<typeof userSearch>("firstName")}
              value={userSearch.firstName}
              maxLength={32}
              onChange={handleChange(setUserSearch)}
            />
          </Col>
          <Col md={2}>
            <TextField
              label={t("common.lastName")}
              name={nameof<typeof userSearch>("lastName")}
              value={userSearch.lastName}
              maxLength={32}
              onChange={handleChange(setUserSearch)}
            />
          </Col>
          {userSession && userSession.roleId !== RoleEnum.Helpdesk &&
            <Col md={4}>
              <TextField
                label={t("common.oib")}
                name={nameof<typeof userSearch>("oib")}
                value={userSearch.oib}
                maxLength={11}
                // type="number"
                onChange={handleChange(setUserSearch)}
              />
            </Col>
          }
        </Row>
        {userSession && userSession.roleId === RoleEnum.Helpdesk &&
          <Row>
            <Col md={4}>
              <TextField
                label={t("common.oib")}
                name={nameof<typeof userSearch>("oib")}
                value={userSearch.oib}
                maxLength={11}
                onChange={handleChange(setUserSearch)}
              />
            </Col>
            <Col md={4}>
              <Select
                multiple
                label={t("common.userRole")}
                name={nameof<typeof userSearch>("userRoles")}
                value={userSearch.userRoles}
                onChange={handleChange(setUserSearch)}
                selectData={codeBookToSelectData(getUserRolesFilter())}
              />
            </Col>
          </Row>
        }
        <Row className="mb-5 mt-5">
          <Col md={12} className="d-flex justify-content-end align-items-center">
            <Button onClick={search}>
              {t("common.search")}
            </Button>
            <Typography color="primary" className={styles.clearFilters + " ml-3"} onClick={clearFilters}>
              {t("common.clearFilters")}
            </Typography>
          </Col>
        </Row>
        <Table {...tableProps} />
        <Row className="mt-5">
          <Col md={12} className="d-flex justify-content-end align-items-center">
            <Button
              className={styles.exportButton}
              outline
              color={"primary"}
              icon={<DownloadIcon />}
              onClick={() => download(ExportTypeEnum.Csv)}
            >
              {t("common.downloadDocumentExtension", { extension: ".csv" })}
            </Button>
            <Button
              className={styles.exportButton}
              outline
              color={"primary"}
              icon={<DownloadIcon />}
              onClick={() => download(ExportTypeEnum.Excel)}
            >
              {t("common.downloadDocumentExtension", { extension: ".xslx" })}
            </Button>
          </Col>
        </Row>
      </>
      <Modal
        type="confirm"
        open={openDeleteConfirmModal}
        cancelText={t("common.cancel")}
        confirmText={t("userRole.confirmDeleteUser")}
        onClose={handleCancelDeleteModal}
        onConfirm={handleDeleteConfirmModal}
      >
        <div>
          <Typography component="h1" variant="largeBold">
            {t("userRole.deleteUserConfirmation")}
          </Typography>
        </div>
      </Modal>

      <Modal
        type="confirm"
        open={openEditConfirmModal}
        cancelText={t("common.cancel")}
        confirmText={t("common.saveChanges")}
        onClose={handleCancelEditModal}
        onConfirm={handleEditConfirmModal}
      >
        <div>
          <Typography component="h1" variant="largeBold">
            {t("userRole.editUserConfirmation")}
          </Typography>
          <Row>
            <Col md={12}>
              <TextField
                label={t("userRole.email")}
                name={nameof<typeof editUserItem>("email")}
                value={editUserItem.email}
                maxLength={255}
                onChange={handleChange(setEditUserItem)}
                errorText={errors?.email?.toString()}
                disabled={!canEdit(editUserItem)}
              />
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <TextField
                label={t("userRole.phone")}
                name={nameof<typeof editUserItem>("phone")}
                value={editUserItem.phone}
                maxLength={32}
                onChange={handleChange(setEditUserItem)}
                errorText={errors?.phone?.toString()}
                disabled={!canEdit(editUserItem)}
              />
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <Select
                label={t("common.userRole")}
                name={nameof<typeof editUserItem>("roleId")}
                value={editUserItem.roleId}
                onChange={handleChange(setEditUserItem)}
                selectData={codeBookToSelectData(getUserRolesForUser(editUserItem))}
                className={styles.transparentTextLabel}
                disabled={!canEditRole(editUserItem)}
              />
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <Checkbox
                label={t("userRole.active")}
                name={nameof<typeof editUserItem>("isActive")}
                value={editUserItem.isActive}
                checked={editUserItem.isActive}
                onChange={handleChange(setEditUserItem)}
                disabled={!canEdit(editUserItem) || editUserItem.userId === userSession?.userId}
              />
            </Col>
          </Row>
        </div>
      </Modal>
      <Modal
        type="message"
        open={openSuccessModal}
        confirmText={t("common.close")}
        onClose={handleCloseSuccessModal}
        onConfirm={handleCloseSuccessModal}
      >
        <div className={styles.modalMessageContentAlignCenter}>
          <Typography component="h1" variant="largeBold">
            {t("common.saveSucceed")}
          </Typography>
        </div>
      </Modal>
    </>
  );
}
